// Phase 3 → 3.5 — Top-level shell for Parts Matrix sub-tab.
//
// AUTHORITY (FF-B):
//   The `partsPricing` / `partsPricingMatrix` / `partsPricingOptiGuard`
//   / `partsPricingInsights` feature flags + `ui_shopProfilePricingMatrix`
//   / `ui_partsPricingGraph` exposures are UI gates only. Server
//   authority for the underlying tables (`shop_pricing_settings`,
//   `shop_pricing_category_rules`, `shop_pricing_matrix_rows`) is
//   enforced by `trg_enforce_parts_pricing_entitlement_*` triggers
//   (migration `20260501130000_ff_b_parts_entitlement_triggers.sql`),
//   which raise `insufficient_privilege` when the shop is not
//   CATALYST/ENTERPRISE tier and lacks the `parts_pricing` addon.
//   A sessionStorage feature-flag override cannot bypass that.
import { Loader2 } from 'lucide-react';
import { useShopPricingConfig } from '@/hooks/pricing/useShopPricingConfig';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { GlobalModeSection } from './GlobalModeSection';
import { QuantityModeSection } from './QuantityModeSection';
import { CategoryRulesTable } from './CategoryRulesTable';
import { MatrixEditor } from './MatrixEditor';
import { MatrixGuardrailsSection } from './MatrixGuardrailsSection';
import { OptiGuardSettingsSection } from './OptiGuardSettingsSection';
import { PricingGraph } from './PricingGraph';
import { OverrideInsightsCard } from './OverrideInsightsCard';

interface Props {
  shopId: string;
}

export function PricingMatrixSection({ shopId }: Props) {
  const { isLoading } = useShopPricingConfig(shopId);
  const optiguardEnabled = useFeatureFlag('partsPricingOptiGuard');
  const insightsEnabled = useFeatureFlag('partsPricingInsights');
  // Call the exposure hook unconditionally, then combine — a `&&` before a hook
  // call short-circuits it and violates rules-of-hooks.
  const pricingGraphExposure = useExposureFlag('ui_partsPricingGraph');
  const showGraph = insightsEnabled && pricingGraphExposure;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GlobalModeSection shopId={shopId} />
      <QuantityModeSection shopId={shopId} />
      <CategoryRulesTable shopId={shopId} />
      <MatrixEditor shopId={shopId} />
      <MatrixGuardrailsSection shopId={shopId} />
      {optiguardEnabled && <OptiGuardSettingsSection shopId={shopId} />}
      {showGraph && <PricingGraph shopId={shopId} />}
      {insightsEnabled && <OverrideInsightsCard shopId={shopId} />}
    </div>
  );
}
